import type { BossBuddyProfile, GetBossBuddiesQuery } from 'src/API';
import { BuddyPermissions } from 'src/API';
import { API, graphqlOperation } from '@aws-amplify/api';
import { getBossBuddies } from 'src/graphql/queries';

/**
 * Team Management sidebar + route: full Admin, Admin (read-only), or primary shopper (not in buddy list).
 */
export function resolveCanAccessTeamManagement(
  profiles: BossBuddyProfile[],
  cognitoSub?: string,
  cognitoUsername?: string
): boolean {
  const me =
    (cognitoSub && profiles.find((p) => p.id === cognitoSub)) ||
    (cognitoUsername && profiles.find((p) => p.id === cognitoUsername)) ||
    undefined;
  if (me) {
    return (
      me.permissionLevel === BuddyPermissions.Admin ||
      me.permissionLevel === BuddyPermissions.AdminRO
    );
  }
  return true;
}

/**
 * Changing roles on Team Management: full Admin only, or primary shopper (not in buddy list).
 */
export function resolveCanEditTeamMemberRoles(
  profiles: BossBuddyProfile[],
  cognitoSub?: string,
  cognitoUsername?: string
): boolean {
  const me =
    (cognitoSub && profiles.find((p) => p.id === cognitoSub)) ||
    (cognitoUsername && profiles.find((p) => p.id === cognitoUsername)) ||
    undefined;
  if (me) {
    return me.permissionLevel === BuddyPermissions.Admin;
  }
  return true;
}

export type TeamManagementAccess = {
  canAccessTeamManagement: boolean;
  canEditTeamMemberRoles: boolean;
};

export async function fetchTeamManagementAccess(
  cognitoUser: {
    attributes?: { sub?: string };
    username?: string;
    signInUserSession?: { idToken?: { payload?: { sub?: string } } };
  }
): Promise<TeamManagementAccess> {
  try {
    const response = (await API.graphql(
      graphqlOperation(getBossBuddies)
    )) as { data: GetBossBuddiesQuery };
    const profiles = response.data.getBossBuddies.bossBuddyProfiles;
    const sub =
      cognitoUser.attributes?.sub ??
      cognitoUser.signInUserSession?.idToken?.payload?.sub;
    return {
      canAccessTeamManagement: resolveCanAccessTeamManagement(
        profiles,
        sub,
        cognitoUser.username
      ),
      canEditTeamMemberRoles: resolveCanEditTeamMemberRoles(
        profiles,
        sub,
        cognitoUser.username
      )
    };
  } catch {
    return {
      canAccessTeamManagement: false,
      canEditTeamMemberRoles: false
    };
  }
}
